import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCurrentUser } from '@tyndo/hooks';
import { useTenantContext } from '@tyndo/hooks';
import { viewRegistry, moduleManifestRegistry, moduleRegistration } from '../registries';
import { resolveSlugToViewId, viewSlugForUrl } from '../utils/urlHelpers';
import { Button, Card, CardContent, EmptyState, ErrorBoundary } from '@tyndo/ui-kit';
import { AlertTriangle, ChevronRight, Construction, ArrowLeft, Sparkles, RefreshCw } from 'lucide-react';
import {
  getParentLocation,
  pushViewLocation,
  type ViewStackEntry,
} from './viewStack';
import { createViewStateAccessor } from './viewState';
import { useModuleAccess } from './ModuleAccessContext';
import { isModulePermitted } from './entitlementGate';

interface ModuleViewProps {
  onError?: (error: Error) => void;
}

/**
 * Module render-error boundary. Delegates the catch/report/reset mechanics to
 * the shared @tyndo/ui-kit ErrorBoundary (consistent with the shell boundary)
 * and renders the module-specific themed card fallback. A "Try again" reset is
 * provided by the shared boundary so a transient render error doesn't strand
 * the operator on a dead card.
 */
function ModuleErrorBoundary({
  children,
  onError,
  moduleName,
}: {
  children: React.ReactNode;
  onError?: (error: Error) => void;
  moduleName?: string;
}) {
  return (
    <ErrorBoundary
      scope={`module:${moduleName ?? 'unknown'}`}
      onError={(error) => {
        console.error('Module rendering error:', error);
        onError?.(error);
      }}
      fallback={({ error, reset }) => (
        <ModuleShell title={moduleName ?? 'Module'} subtitle="Render error">
          <Card variant="elevated" className="max-w-2xl">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-alarm-critical/10 text-alarm-critical">
                  <AlertTriangle className="h-5 w-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-foreground">
                    Couldn't render this view
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {error.message || 'Unknown error'}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={reset}
                    className="h-auto whitespace-normal [&_svg]:size-3.5 mt-3 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-base px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Try again
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </ModuleShell>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * ModuleShell — every module view (federated or fallback) renders inside this
 * wrapper so the whole UI feels cohesive even when individual modules use
 * bespoke internal layouts. Provides token-driven background + max-width
 * gutter + optional eyebrow/title slot.
 *
 * Export so federated modules can opt-in for matching chrome — but they don't
 * have to; if they render their own layout, this just provides the outer
 * surface and gutter.
 */
export function ModuleShell({
  title,
  subtitle,
  eyebrow,
  children,
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  eyebrow?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-full bg-surface-base">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6 lg:py-8">
        {(title || eyebrow) && (
          <div className="mb-6">
            {eyebrow && <p className="t-eyebrow">{eyebrow}</p>}
            {title && (
              <h1 className="mt-1.5 text-2xl lg:text-3xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-1.5 text-sm lg:text-base text-muted-foreground">{subtitle}</p>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export default function ModuleView({ onError }: ModuleViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { moduleId, viewType, model, viewId, entityId } = useParams<{
    moduleId?: string;
    viewType?: string;
    model?: string;
    viewId?: string;
    entityId?: string;
  }>();

  // Deep-link entitlement gate. A URL typed, bookmarked, or pasted by the
  // operator is an unauthenticated-by-default navigation path: the fact that a
  // module never appeared in the nav means nothing here. Nothing about the
  // module is resolved, preloaded, or rendered unless the gate says `ready`
  // AND the module is in the permitted set.
  const moduleAccess = useModuleAccess();
  const moduleAllowed = isModulePermitted(moduleAccess, moduleId);
  const accessResolved = moduleAccess.state !== 'loading';

  // Scope the drill-through stack by tenant + user so one operator's
  // navigation trail never leaks into another's on a shared workstation.
  const { tenantId } = useTenantContext();
  const currentUser = useCurrentUser();
  const stackScope = useMemo(
    () => ({ tenantId, userId: currentUser?.sub ?? currentUser?.email ?? null }),
    [tenantId, currentUser?.sub, currentUser?.email],
  );

  const [ViewComponent, setViewComponent] = useState<React.ComponentType<Record<string, unknown>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Manual retry token. Bumped by the Retry button so a transient load failure
  // (federated dev server briefly down, a chunk that 404'd during a deploy, a
  // componentName that resolved a beat too early) can be re-attempted without a
  // full page reload. Capped below so a permanently-broken view doesn't let the
  // operator hammer a dead endpoint forever.
  const MAX_RETRIES = 3;
  const [retryToken, setRetryToken] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  // Snapshot of the ViewRegistry, refreshed on every registry mutation so a
  // Pass 1 (gateway-stub) miss is retried automatically once Pass 2 (federated
  // module) registers the real view. Without this, deep-links rendered before
  // federation completes get stuck on the "View not yet available" empty state
  // forever.
  const [registryViews, setRegistryViews] = useState(() =>
    viewRegistry.listViews()
  );
  useEffect(() => {
    return viewRegistry.subscribe(() => {
      setRegistryViews(viewRegistry.listViews());
    });
  }, []);

  // Is the module named in the URL still registering?
  //
  // The gateway manifest does not always carry view descriptors (the hermetic
  // mock stack never does, and a real deployment is mid-fan-out for as long as
  // Pass 2 takes across ~60 modules), so during boot a deep link resolves NO
  // component for a module that is seconds away from registering one. That is a
  // TRANSIENT condition, and rendering the settled "this page isn't wired up to
  // a federated component yet" verdict for it states something false: the shell
  // has not finished asking. This flag is the distinction, and the registering
  // branch below is what it renders instead.
  const [moduleRegistering, setModuleRegistering] = useState(() =>
    moduleRegistration.isPending(moduleId),
  );
  useEffect(() => {
    const sync = () => setModuleRegistering(moduleRegistration.isPending(moduleId));
    sync();
    return moduleRegistration.subscribe(sync);
  }, [moduleId]);

 // The new clean URL form passes the SHORT slug as :viewId
  // (e.g. `transactions`), so we must promote it back to the full
  // canonical viewId (`inventory.view.transactions`) before the registry
  // can resolve the component. Legacy URLs that pass the full viewId
  // round-trip unchanged because resolveSlugToViewId checks exact match
  // first. Recomputes when the registry mutates so a Pass-2 federated
  // view registration unblocks deep-links rendered before federation
  // completed.
  const resolveKey = useMemo(() => {
    const raw = viewId || model;
    if (!raw) return '';
    const allViewIds = registryViews
      .filter((v) => !moduleId || v.moduleId === moduleId)
      .map((v) => v.viewId);
    return resolveSlugToViewId(raw, allViewIds);
  }, [viewId, model, moduleId, registryViews]);
  const resolveType = viewId ? 'viewId' : viewType;

  // Identity of the thing this route is currently showing.
  //
  // The resolve effect below deliberately re-runs on EVERY ViewRegistry
  // mutation — that is how a deep link rendered during Pass 1 picks up its real
  // component the moment Pass 2 registers it. What it must NOT do is drop back
  // into the loading state each time, because the registry mutates once per
  // registered view: Pass 2 does that for every view of every module (thousands
  // of times across ~60 modules, and twice over under React StrictMode in dev),
  // and the remote-health scheduler adds more whenever it re-attempts a degraded
  // remote. Each of those flipped `loading` true, which unmounted the mounted
  // view and mounted it again a tick later.
  //
  // That tore the surface down under the operator: a click in flight lands on a
  // node that has just been replaced (Playwright reports it verbatim — "element
  // was detached from the DOM, retrying", in a loop), a half-typed input loses
  // its value, and every piece of component state — selected tab, scroll
  // position, an open dialog — resets.
  //
  // Re-RESOLVING on a bump still happens; only the unmount is removed. If a bump
  // genuinely changed which component this viewId maps to, the swap below still
  // replaces it.
  // JSON.stringify, not a literal NUL delimiter. A raw `\0` is unambiguous — it
  // cannot occur inside any of the three parts — but it also made this FILE read
  // as binary to text scanners: `file(1)` reported `data`, and grep/ripgrep skip
  // NUL-bearing files unless forced. Every grep-based audit over the shell was
  // therefore silently blind to this file, which is exactly the
  // pass-over-the-thing-you-meant-to-check failure the architecture gates exist to
  // prevent. An array encoding is equally collision-proof (the encoder escapes the
  // separator) and keeps the file plain text.
  const targetKey = JSON.stringify([moduleId ?? '', resolveType ?? '', resolveKey]);
  const mountedTargetRef = useRef<string | null>(null);

  const moduleEntry = moduleId ? moduleManifestRegistry.get(moduleId) : undefined;
  const moduleName = moduleEntry?.name ?? moduleId ?? 'Module';

  // The view's metadata (label, etc.) — pulled from the registry so the
  // shell can render a coherent header even before the federated bundle loads.
  // Re-evaluates when the registry mutates so Pass 2 federated metadata
  // wins over the synthetic Pass 1 label.
  const viewMeta = useMemo(() => {
    if (!resolveKey) return null;
    return (
      registryViews.find(
        (v) => v.viewId === resolveKey
      ) ?? null
    );
  }, [resolveKey, registryViews]);

  // Optional entity preloader. A view that renders a specific entity (deep-link
  // with :entityId) can declare an `entityPreloader(entityId) => Promise` on its
  // view definition. When present the shell validates the entity BEFORE mounting
  // the federated component, so a missing/failed entity surfaces a themed
  // "not found" state instead of flashing the full view shell, then having the
  // component itself discover the 404 a beat later. Read defensively because the
  // shared view-definition type doesn't (yet) declare this field on every shape;
  // views that don't ship a preloader render exactly as before.
  type EntityPreloader = (
    entityId: string,
    ctx: { moduleId?: string; viewId?: string; signal: AbortSignal },
  ) => Promise<unknown>;
  const entityPreloader = (viewMeta as { entityPreloader?: EntityPreloader } | null)
    ?.entityPreloader;
  // States: 'idle' (no preload needed), 'loading', 'ready', 'notfound', 'error'.
  const [preloadState, setPreloadState] = useState<
    'idle' | 'loading' | 'ready' | 'notfound' | 'error'
  >('idle');
  const [preloadError, setPreloadError] = useState<string | null>(null);

  useEffect(() => {
    // Only gate when the target is an entity-scoped deep-link AND the resolved
    // view actually declares a preloader. Otherwise the view renders straight
    // through (preserving the previous behaviour for the common list/dashboard
    // case and for views that own their own not-found handling).
    // An unpermitted (or not-yet-resolved) module must not have its entity
    // preloader run: that would issue a data request for a module the operator
    // may not open.
    if (!moduleAllowed || !entityId || typeof entityPreloader !== 'function') {
      setPreloadState('idle');
      setPreloadError(null);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setPreloadState('loading');
    setPreloadError(null);
    Promise.resolve()
      .then(() =>
        entityPreloader(entityId, { moduleId, viewId: resolveKey || undefined, signal: controller.signal }),
      )
      .then((result) => {
        if (cancelled) return;
        // A preloader may signal "not found" either by resolving null/undefined
        // or by throwing/rejecting with a 404-ish error (handled below).
        setPreloadState(result === null || result === undefined ? 'notfound' : 'ready');
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        const status = (err as { status?: number; statusCode?: number } | undefined);
        const code = status?.status ?? status?.statusCode;
        const message = err instanceof Error ? err.message : String(err);
        if (code === 404 || /not[\s-]?found/i.test(message)) {
          setPreloadState('notfound');
        } else {
          setPreloadState('error');
          setPreloadError(message);
        }
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [entityId, entityPreloader, moduleId, resolveKey, retryToken, moduleAllowed]);

  useEffect(() => {
    const loadView = async () => {
      // Never resolve, import, or mount a view for a module the gate hasn't
      // permitted — a denied deep link must not even fetch the bundle.
      if (!moduleAllowed) {
        mountedTargetRef.current = null;
        setViewComponent(null);
        setError(null);
        setLoading(false);
        return;
      }
      // Already showing this exact target: re-resolve in place, keeping the
      // mounted component on screen throughout. Only a genuinely new target
      // (or a target that has nothing mounted yet) shows the spinner.
      const alreadyMounted = mountedTargetRef.current === targetKey;
      try {
        if (!alreadyMounted) setLoading(true);
        setError(null);

        if (!resolveKey) {
          mountedTargetRef.current = null;
          setError('Missing view identifier');
          return;
        }

        const component = await viewRegistry.resolve(
          resolveKey,
          resolveType || 'dashboard',
          moduleId,
        );
        if (!component) {
          mountedTargetRef.current = null;
          setError(`No view found for: ${resolveKey}`);
          return;
        }

        setViewComponent(() => component);
        mountedTargetRef.current = targetKey;
      } catch (err) {
        mountedTargetRef.current = null;
        const message = err instanceof Error ? err.message : 'Failed to load view';
        setError(message);
        onError?.(new Error(message));
      } finally {
        setLoading(false);
      }
    };

    loadView();
  }, [
    resolveKey,
    resolveType,
    moduleId,
    targetKey,
    onError,
    registryViews,
    retryToken,
    moduleAllowed,
  ]);

  // Reset the retry budget whenever the navigation target changes — moving to a
  // different view should start with a fresh set of attempts.
  useEffect(() => {
    setRetryCount(0);
  }, [resolveKey, resolveType, moduleId]);

  const canRetry = retryCount < MAX_RETRIES;
  const handleRetry = () => {
    if (!canRetry) return;
    setRetryCount((n) => n + 1);
    setRetryToken((n) => n + 1);
  };

  // Record this location in the per-(tenant,user) drill-through stack so the
  // back control has a coherent target and the breadcrumb knows its parent.
  // Only push once the view has actually resolved (no point recording a
  // not-found branch). Re-runs when the path changes.
  useEffect(() => {
    if (loading || error || !ViewComponent) return;
    // Don't record a branch that never actually rendered the entity — a
    // pending or failed entity-preload would otherwise pollute the back stack.
    if (preloadState === 'loading' || preloadState === 'notfound' || preloadState === 'error') return;
    const label =
      (entityId ? `${viewMeta?.viewName ?? moduleName} · ${entityId}` : viewMeta?.viewName) ??
      moduleName;
    const entry: ViewStackEntry = {
      path: location.pathname,
      label,
      moduleId,
      viewId: resolveKey || undefined,
      entityId,
    };
    pushViewLocation(stackScope, entry);
  }, [
    location.pathname,
    loading,
    error,
    ViewComponent,
    entityId,
    moduleId,
    resolveKey,
    viewMeta?.viewName,
    moduleName,
    stackScope,
    preloadState,
  ]);

  // Resolve the back target. Prefer the previous distinct stack entry; when a
  // detail view was deep-linked cold (shallow stack) fall back to the owning
  // module's default/list view so "back" never bounces out of the app.
  const backTarget = useMemo<{ path: string; label: string } | null>(() => {
    const parent = getParentLocation(stackScope);
    if (parent && parent.path !== location.pathname) {
      return { path: parent.path, label: parent.label };
    }
    // Deep-linked detail with no in-app parent: synthesize the module's
    // list/default view as the back destination.
    if (entityId && moduleId) {
      const moduleViews = registryViews.filter((v) => v.moduleId === moduleId);
      const listView =
        moduleViews.find((v) => v.isDefault) ??
        moduleViews.find((v) => v.viewId !== resolveKey) ??
        moduleViews[0];
      if (listView) {
        return {
          path: `/${moduleId}/${viewSlugForUrl(listView.viewId)}`,
          label: listView.viewName ?? moduleName,
        };
      }
    }
    return null;
  }, [stackScope, location.pathname, entityId, moduleId, resolveKey, moduleName, registryViews]);

  const handleBack = () => {
    if (backTarget) {
      navigate(backTarget.path);
    } else {
      navigate(-1);
    }
  };

  // Shell-level view-state accessor. A view opts in by declaring a
  // `viewStateKey` in its UiViewDefinition; the shell hands the component a
  // persistence accessor scoped by tenant+user+key so operators get saved
  // sort/filter/pagination without each module wiring its own localStorage.
  const viewStateKey = (viewMeta as { viewStateKey?: string } | null)?.viewStateKey;
  const viewState = useMemo(
    () => createViewStateAccessor(stackScope, viewStateKey),
    [stackScope, viewStateKey],
  );

  // ── Deep-link entitlement gate ────────────────────────────────────────
  // Ordered before every other branch so a denied module can never fall
  // through to a state that names it, retries it, or renders it.
  if (!accessResolved) {
    return (
      <ModuleShell title="Checking your access…">
        <div className="flex items-center justify-center py-16">
          <div
            role="status"
            aria-live="polite"
            data-testid="moduleview-entitlements-loading"
            className="text-center"
          >
            <div className="animate-spin motion-reduce:animate-none rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              Confirming you can open this module…
            </p>
          </div>
        </div>
      </ModuleShell>
    );
  }

  if (!moduleAllowed) {
    // Deliberately does NOT render the manifest display name, description, or
    // view label — only the identifier the operator already typed — so a
    // denied deep link discloses nothing about the module.
    return (
      <ModuleShell title="Not available">
        <div data-testid="moduleview-module-forbidden">
          <EmptyState
            icon={AlertTriangle}
            title="You don't have access to this module"
            description={
              moduleAccess.state === 'unavailable'
                ? "Your access couldn't be verified, so this page wasn't opened. Try again, or contact your administrator."
                : 'This module is not part of your workspace entitlements. Contact your administrator if you need access.'
            }
            action={
              <Button
                type="button"
                onClick={() => navigate('/browse')}
                className="h-auto whitespace-normal [&_svg]:size-3.5 shadow-none inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Browse your modules
              </Button>
            }
          />
        </div>
      </ModuleShell>
    );
  }

  // ── Still registering ─────────────────────────────────────────────────
  // Ordered BEFORE the error branch on purpose. While the module's own
  // registration is in flight, "no view found" is not an answer — it is the
  // absence of one — so this renders a loading state that says exactly that,
  // and the error branch below only ever speaks once the module has settled.
  //
  // Harness coupling is deliberate and explicit: `tests/e2e/tests/helpers/
  // viewReady.ts` synchronises on the two testids stamped here and on the
  // loading branch. Renaming either without updating that helper silently turns
  // the suite's "wait until the view is mounted" into a no-op.
  if (error && moduleRegistering) {
    return (
      <ModuleShell eyebrow={moduleName} title={viewMeta?.viewName ?? moduleName}>
        <div className="flex items-center justify-center py-16">
          <div
            role="status"
            aria-live="polite"
            data-testid="moduleview-module-registering"
            className="text-center"
          >
            <div className="animate-spin motion-reduce:animate-none rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              Loading the {moduleName} module…
            </p>
          </div>
        </div>
      </ModuleShell>
    );
  }

  if (error) {
    // The most common cause of this branch is a federated module that hasn't
    // started its dev server yet (404 on the bundle), or a manifest that
    // points at a view the module doesn't actually export. Render a themed
    // "coming soon" state instead of red HTML so the UI stays cohesive.
    const looksLikeMissingPage =
      error.includes('does not export') ||
      error.includes('No view found') ||
      error.includes('not configured') ||
      error.includes('Loading chunk');

    return (
      <ModuleShell
        eyebrow={moduleName}
        title={viewMeta?.viewName ?? 'View not yet available'}
        subtitle={
          looksLikeMissingPage
            ? "This page isn't wired up to a federated component yet."
            : undefined
        }
      >
        <EmptyState
          icon={looksLikeMissingPage ? Construction : AlertTriangle}
          title={
            looksLikeMissingPage
              ? 'Coming soon'
              : 'Something went wrong loading this view'
          }
          description={
            looksLikeMissingPage
              ? `The ${moduleName} module exposes this view in its manifest, but no UI component is exported for it yet. Once the module ships its component, this page will fill in automatically.`
              : canRetry
                ? error
                : `${error} — retried ${retryCount} time${retryCount === 1 ? '' : 's'} without success. Try browsing the module catalogue instead.`
          }
          action={
            <div className="flex items-center gap-2">
              {/* A failed lazy-load is often transient (dev server restart, a
                  chunk that 404'd mid-deploy, a federated bundle that landed a
                  beat late). Offer an in-place retry — capped — before sending
                  the operator off to the catalogue. We don't show Retry for the
                  "coming soon" case: nothing is wired up to load, so retrying
                  would just fail the same way. */}
              {!looksLikeMissingPage && canRetry && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRetry}
                  className="h-auto whitespace-normal [&_svg]:size-3.5 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-base px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Retry
                </Button>
              )}
              <Button
                type="button"
                onClick={() => navigate('/browse')}
                className="h-auto whitespace-normal [&_svg]:size-3.5 shadow-none inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Browse all modules
              </Button>
            </div>
          }
        />
      </ModuleShell>
    );
  }

  if (loading || !ViewComponent) {
    return (
      <ModuleShell eyebrow={moduleName} title={viewMeta?.viewName ?? 'Loading…'}>
        <div className="flex items-center justify-center py-16">
          <div
            role="status"
            aria-live="polite"
            data-testid="moduleview-view-loading"
            className="text-center"
          >
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Loading view…</p>
          </div>
        </div>
      </ModuleShell>
    );
  }

  // Entity-preload gate. When a view declared an `entityPreloader`, hold the
  // full view shell back until the entity is validated so a deep-link to a
  // missing entity never flashes the component before its own 404. The
  // back-target is still computed above, so "not found" keeps a coherent way
  // back to the owning module's list.
  if (preloadState === 'loading') {
    return (
      <ModuleShell
        eyebrow={moduleName}
        title={viewMeta?.viewName ?? moduleName}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Loading {entityId}…</p>
          </div>
        </div>
      </ModuleShell>
    );
  }

  if (preloadState === 'notfound' || preloadState === 'error') {
    const isNotFound = preloadState === 'notfound';
    return (
      <ModuleShell
        eyebrow={moduleName}
        title={viewMeta?.viewName ?? moduleName}
      >
        <EmptyState
          icon={AlertTriangle}
          title={isNotFound ? 'Not found' : 'Couldn’t load this record'}
          description={
            isNotFound
              ? `No ${viewMeta?.viewName ?? moduleName} record matches “${entityId}”. It may have been deleted, or the link is out of date.`
              : preloadError ?? 'The record could not be loaded.'
          }
          action={
            <div className="flex items-center gap-2">
              {!isNotFound && canRetry && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRetry}
                  className="h-auto whitespace-normal [&_svg]:size-3.5 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-base px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Retry
                </Button>
              )}
              {backTarget ? (
                <Button
                  type="button"
                  onClick={() => navigate(backTarget.path)}
                  className="h-auto whitespace-normal [&_svg]:size-3.5 shadow-none inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to {backTarget.label}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => navigate('/browse')}
                  className="h-auto whitespace-normal [&_svg]:size-3.5 shadow-none inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Browse all modules
                </Button>
              )}
            </div>
          }
        />
      </ModuleShell>
    );
  }

  // Drill-through chrome: a back control + an entity-level breadcrumb segment.
  // Shown when the operator is on a detail view (entityId present) or there is
  // a real parent in the stack — so deep-linking into a detail always has a
  // way back to the parent list.
  const showDrillHeader = Boolean(entityId) || Boolean(backTarget);

  return (
    <ModuleErrorBoundary onError={onError} moduleName={moduleName}>
      {showDrillHeader && (
        <div className="border-b border-border/60 bg-surface-raised/60">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 lg:px-8 py-2.5">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              className="h-auto whitespace-normal [&_svg]:size-3.5 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-base px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent"
              aria-label={backTarget ? `Back to ${backTarget.label}` : 'Go back'}
              title={backTarget ? `Back to ${backTarget.label}` : 'Go back'}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </Button>
            <nav
              aria-label="Drill-through breadcrumb"
              className="flex min-w-0 items-center gap-1 overflow-auto text-xs"
            >
              {backTarget && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => navigate(backTarget.path)}
                    className="h-auto text-xs font-normal hover:bg-transparent whitespace-nowrap rounded px-1.5 py-0.5 text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {backTarget.label}
                  </Button>
                  <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/50" />
                </>
              )}
              <span className="whitespace-nowrap px-1.5 py-0.5 font-medium text-foreground">
                {entityId
                  ? `${viewMeta?.viewName ?? moduleName} · ${entityId}`
                  : viewMeta?.viewName ?? moduleName}
              </span>
            </nav>
          </div>
        </div>
      )}
      <Suspense
        fallback={
          <ModuleShell eyebrow={moduleName}>
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          </ModuleShell>
        }
      >
        <ViewComponent
          moduleId={moduleId}
          viewType={viewType}
          model={model}
          entityId={entityId}
          viewState={viewState}
        />
      </Suspense>
    </ModuleErrorBoundary>
  );
}
